import React, { useState } from "react";
import { Link, useLocation } from "wouter";
import { useCurrentUser } from "@/lib/auth";
import { LogOut, Menu, type LucideIcon } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import IdleLogoutGuard from "@/components/IdleLogoutGuard";

export interface BackofficeNavItem {
  href: string;
  label: string;
  icon: LucideIcon;
}

export interface BackofficeAction {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  testId: string;
}

interface BackofficeShellProps {
  title: string;
  icon: LucideIcon;
  navItems: BackofficeNavItem[];
  /** Sélecteur de scope (année, diocèse…) affiché dans le header desktop et le menu mobile. */
  selector?: React.ReactNode;
  /** Contenu additionnel à droite du titre (ex. nom de l'école), masqué sur mobile. */
  subtitle?: React.ReactNode;
  /** Boutons additionnels du header (ex. « Mon compte »). */
  actions?: BackofficeAction[];
  children: React.ReactNode;
}

export default function BackofficeShell({
  title,
  icon: TitleIcon,
  navItems,
  selector,
  subtitle,
  actions = [],
  children,
}: BackofficeShellProps) {
  const [location] = useLocation();
  const { user, logout } = useCurrentUser();
  const [menuOpen, setMenuOpen] = useState(false);

  const renderNav = (mobile: boolean) =>
    navItems.map((it) => {
      const active = location === it.href;
      const Icon = it.icon;
      return (
        <Link
          key={it.href}
          href={it.href}
          onClick={mobile ? () => setMenuOpen(false) : undefined}
          className={`flex items-center gap-2.5 px-3 py-2 text-sm rounded font-medium transition-colors ${
            active ? "bg-navy text-white" : "text-slate-700 hover:bg-slate-100"
          }`}
          data-testid={`${mobile ? "mnav" : "nav"}-${it.href.replace(/\//g, "-")}`}
        >
          <Icon className="w-4 h-4" />
          {it.label}
        </Link>
      );
    });

  const identity = user ? (
    <>
      {user.prenoms} {user.nom} · <span className="text-gold">{user.roles?.join(", ")}</span>
    </>
  ) : null;

  return (
    <div className="min-h-screen bg-slate-50 font-sans" style={{ fontFamily: "Inter, ui-sans-serif" }}>
      {/* Auto-déconnexion par inactivité (back-office = toujours authentifié). */}
      <IdleLogoutGuard />
      <header className="bg-navy text-white border-b border-navy/40">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMenuOpen(true)}
              className="md:hidden -ml-1 p-1.5 rounded hover:bg-white/10 shrink-0"
              aria-label="Ouvrir le menu"
              data-testid="button-mobile-menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <TitleIcon className="w-5 h-5 text-gold shrink-0" />
            <span className="font-bold tracking-tight truncate">{title}</span>
            {subtitle}
          </div>
          <div className="hidden md:flex items-center gap-4 text-sm shrink-0">
            {selector}
            <span className="text-white/80">{identity}</span>
            {actions.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.testId}
                  onClick={a.onClick}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-white/20 rounded hover:bg-white/10 text-xs"
                  data-testid={a.testId}
                >
                  <Icon className="w-3.5 h-3.5" /> {a.label}
                </button>
              );
            })}
            <button
              onClick={logout}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-white/20 rounded hover:bg-white/10 text-xs"
              data-testid="button-logout"
            >
              <LogOut className="w-3.5 h-3.5" /> Déconnexion
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-[1400px] mx-auto flex">
        <aside className="hidden md:block w-60 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-3.5rem)] py-4">
          <nav className="flex flex-col gap-0.5 px-2">{renderNav(false)}</nav>
        </aside>
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col gap-0 [&>button]:text-white">
          <SheetHeader className="bg-navy px-4 py-4 pr-10 text-left">
            <SheetTitle className="flex items-center gap-2 text-white text-base font-bold tracking-tight">
              <TitleIcon className="w-5 h-5 text-gold shrink-0" /> {title}
            </SheetTitle>
          </SheetHeader>
          <nav className="flex flex-col gap-0.5 px-2 py-4 overflow-y-auto" data-testid="mobile-nav">
            {renderNav(true)}
          </nav>
          <div className="mt-auto border-t border-navy/20 bg-navy text-white px-4 py-4 space-y-3 text-sm">
            {selector && <div>{selector}</div>}
            {identity && <div className="text-white/80">{identity}</div>}
            <div className="flex flex-wrap gap-2">
              {actions.map((a) => {
                const Icon = a.icon;
                return (
                  <button
                    key={a.testId}
                    onClick={() => {
                      setMenuOpen(false);
                      a.onClick();
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-white/20 rounded hover:bg-white/10 text-xs"
                    data-testid={`${a.testId}-mobile`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {a.label}
                  </button>
                );
              })}
              <button
                onClick={logout}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-white/20 rounded hover:bg-white/10 text-xs"
                data-testid="button-logout-mobile"
              >
                <LogOut className="w-3.5 h-3.5" /> Déconnexion
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
